/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Stack } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDemoCustomContent } from 'src/core/services/telemetry/eventsNamesTemplate';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { DemoHeader } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoHeader';
import { DemoPageAgreement } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoPageAgreement';
import { DocumentInputStep } from 'src/languageStudio/appShell/prebuiltDemos/components/DocumentInputStep';
import { NextStepsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsStep';
import { ResultsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/ResultsStep';
import { TaForHealthLabelerContainer } from 'src/languageStudio/appShell/prebuiltDemos/taForHealth/TaForHealthLabelerContainer';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { labelDescriptionIconClassName } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import { taForHealthSamples } from 'src/languageStudio/appShell/prebuiltDemos/utils/demoSamples';
import {
    taForHealthApiReferenceLink,
    taForHealthDocLink,
    taForHealthOverviewLink,
    taForHealthSamplesLink,
    taForHealthSdkLink,
    taForHealthVersionsLink,
    textAnalyticsPricingLink
} from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';
import {
    Language,
    taForHealthDemoGAModelSupportedLanguages,
    taForHealthDemoPreviewModelSupportedLanguages
} from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { TryoutModelVersion } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedModelVerion';
import { useTextAnalyticsCapabilities } from 'src/languageStudio/core/hooks/useTextAnalyticsCapabilities';
import { DocumentHealthcareEntitiesSwagger } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import { isDocumentSwaggerRtl, isDocumentSwaggerWordTokenized } from 'src/languageStudio/core/utils/demosUtils';

const messages = defineMessages({
    model: { id: 'languageStudio.taForHealthDemoPage.model', defaultMessage: 'Model' },
    preview: { id: 'languageStudio.taForHealthDemoPage.preview', defaultMessage: 'Preview' },
    pageHeader: { id: 'languageStudio.taForHealthDemoPage.pageHeader', defaultMessage: 'Extract health information' },
    generallyAvailable: { id: 'languageStudio.taForHealthDemoPage.generallyAvailable', defaultMessage: 'Generally Available' },
    optionsLabel: { id: 'languageStudio.taForHealthDemoPage.optionsLabel', defaultMessage: 'Return output in FHIR structure' },
    selectResourceLabel: { id: 'languageStudio.taForHealthDemoPage.selectResourceLabel', defaultMessage: 'Select your Azure resource' },
    hideCardsMessage: { id: 'languageStudio.taForHealthDemoPage.hideCardsMessage', defaultMessage: 'Turn on cards to see named entities' },
    disabledToggleToolTip: {
        id: 'languageStudio.taForHealthDemoPage.disabledToggleToolTip',
        defaultMessage: 'FHIR structure is only supported for English and the Preview API.'
    },
    optionToolTipContent: {
        id: 'languageStudio.taForHealthDemoPage.optionToolTipContent',
        defaultMessage: 'Output is returned using Fast Healthcare Interoperability Resources format.'
    },
    capabilityDescription: {
        id: 'languageStudio.taForHealthDemoPage.capabilityDescription',
        defaultMessage: `Text Analytics for health is a feature that extracts and labels relevant medical information from unstructured texts such as doctor's notes,
discharge summaries, clinical documents, and electronic health records.`
    },
    emptyState: {
        id: 'languageStudio.taForHealthDemoPage.emptyState',
        defaultMessage: 'Your results will appear here once you enter or upload some text and press Run. The file format needs to be .txt'
    },
    namedEntitiesIdentifiedTitle: {
        id: 'languageStudio.taForHealthDemoPage.namedEntitiesIdentifiedTitle',
        defaultMessage: 'Named entities identified'
    }
});

export const TaForHealthDemoPage = observer(() => {
    const { localize } = useLocalization();
    const { getHealthAnalysis, getCurlCommand, healthAnalysis, tracker, resetResults } = useTextAnalyticsCapabilities();

    const [document, setDocument] = React.useState('');
    const [selectedSampleTitle, setSelectedSampleTitle] = React.useState<string>(null);
    const [language, setLanguage] = React.useState<Language['code']>('en');
    const [languageAutoDetection, setLanguageAutoDetection] = React.useState(false);
    const [modelVersion, setModelVersion] = React.useState<TryoutModelVersion>('GA');
    const [apiVersion, setApiVersion] = React.useState<TryoutModelVersion>('Preview');
    const [fhirStructure, setFhirStructure] = React.useState<boolean>(false);

    const isFHIRDisabled = apiVersion !== 'Preview' || language !== 'en';
    const languageList = React.useMemo(
        () => (modelVersion === 'GA' ? taForHealthDemoGAModelSupportedLanguages : taForHealthDemoPreviewModelSupportedLanguages),
        [modelVersion]
    );

    const modelVersionOptions: IDropdownOption[] = [
        {
            key: 'GA',
            text: localize(messages.generallyAvailable),
            data: {
                version: LuisConstants.Demos.TaForHealthModelVersions.GA,
                secondaryText: `${localize(messages.model)}: ${LuisConstants.Demos.TaForHealthModelVersions.GA}`
            }
        }
    ];

    const apiVersionOptions: IDropdownOption[] = [
        {
            key: 'GA',
            text: localize(messages.generallyAvailable),
            data: {
                version: LuisConstants.Demos.ApiVersions.GA,
                secondaryText: `${localize(messages.model)}: ${LuisConstants.Demos.ApiVersions.GA}`
            }
        },
        {
            key: 'Preview',
            text: localize(messages.preview),
            data: {
                version: LuisConstants.Demos.ApiVersions.Preview,
                secondaryText: `${localize(messages.model)}: ${LuisConstants.Demos.ApiVersions.Preview}`
            }
        }
    ];

    const handleDocumentChange = (newDocument: string) => {
        if (healthAnalysis) {
            resetResults();
        }
        setDocument(newDocument);
    };

    const getVersion = (version: TryoutModelVersion, isApiVersion: boolean) => {
        if (isApiVersion) {
            return version === 'GA' ? LuisConstants.Demos.ApiVersions.GA : LuisConstants.Demos.ApiVersions.Preview;
        }
        return version === 'GA' ? LuisConstants.Demos.TaForHealthModelVersions.GA : LuisConstants.Demos.TaForHealthModelVersions.Preview;
    };

    const handleRunDemo = () => {
        if (!selectedSampleTitle) {
            logDemoCustomContent('Health information');
        }

        getHealthAnalysis({
            document,
            language,
            fhirStructure,
            sampleTitle: selectedSampleTitle,
            apiVersion: getVersion(apiVersion, true),
            autoDetectLanguage: languageAutoDetection,
            modelVersion: getVersion(modelVersion, false)
        });
    };

    React.useEffect(() => {
        if (isFHIRDisabled && fhirStructure) {
            setFhirStructure(false);
        }
    }, [isFHIRDisabled]);

    const taForHealthOptions = (
        <Stack styles={{ root: { flex: 2 } }} tokens={{ childrenGap: 17 }}>
            <Stack horizontal tokens={{ childrenGap: 5 }}>
                <LuisText fontWeight={500}>{localize(commonDemosMessages.options)}</LuisText>
                <LuisText fontWeight={500}>{`(${localize(messages.preview)})`}</LuisText>
            </Stack>
            <LuisTooltip content={isFHIRDisabled && localize(messages.disabledToggleToolTip)} directionalHint={DirectionalHint.leftCenter}>
                <Toggle
                    checked={fhirStructure}
                    onText={localize(commonDemosMessages.onText)}
                    offText={localize(commonDemosMessages.offText)}
                    onChange={(_, isChecked) => setFhirStructure(isChecked)}
                    disabled={isFHIRDisabled}
                    label={
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                            <LuisText variant="medium" fontWeight={500} styles={{ root: { whiteSpace: 'nowrap' } }}>
                                {localize(messages.optionsLabel)}
                            </LuisText>
                            <CalloutButton
                                iconClassName={labelDescriptionIconClassName}
                                content={localize(messages.optionToolTipContent)}
                            />
                        </Stack>
                    }
                />
            </LuisTooltip>
        </Stack>
    );

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <DemoHeader
                getSdkLink={taForHealthSdkLink}
                featureName="Health information"
                documentationLink={taForHealthDocLink}
                learnMoreLink={taForHealthOverviewLink}
                supportedPlatforms={['docker', 'cloud']}
                demoTitle={localize(messages.pageHeader)}
                gitHubSamplesLink={taForHealthSamplesLink}
                demoDescription={localize(messages.capabilityDescription)}
                demoVersion={apiVersion === 'GA' ? LuisConstants.Demos.ApiVersions.GA : LuisConstants.Demos.ApiVersions.Preview}
            />

            <DocumentInputStep
                isPaidDemo
                document={document}
                selectedLanguage={language}
                languagesList={languageList}
                onLanguageChange={setLanguage}
                selectedApiVersion={apiVersion}
                featureName="Health information"
                samplesList={taForHealthSamples}
                onApiVersionChange={setApiVersion}
                selectedModelVersion={modelVersion}
                acceptedResourceType="TextAnalytics"
                disableVersionsModalSelection={false}
                onModelVersionChange={setModelVersion}
                onDocumentChange={handleDocumentChange}
                selectedSampleTitle={selectedSampleTitle}
                isTextRTL={isDocumentSwaggerRtl(language)}
                supportedApiVersionOptions={apiVersionOptions}
                versionsLearnMoreLink={taForHealthVersionsLink}
                supportedRegionLink={taForHealthApiReferenceLink}
                supportedModelVersionOptions={modelVersionOptions}
                onRenderHorizontalOptions={() => taForHealthOptions}
                resourceDropdownLabel={localize(messages.selectResourceLabel)}
                onSampleClick={sample => setSelectedSampleTitle(sample?.title)}
                onLanguageAutoDetectionClick={languageList.length > 1 && setLanguageAutoDetection}>
                <DemoPageAgreement
                    isPaidDemo
                    onRunDemoClick={handleRunDemo}
                    isRunDisabled={!document.length}
                    featureName="Health information"
                    acceptedResourceType="TextAnalytics"
                    pricingLink={textAnalyticsPricingLink}
                />
            </DocumentInputStep>

            <ResultsStep<DocumentHealthcareEntitiesSwagger>
                results={healthAnalysis}
                isLoading={tracker.isRunning}
                featureName="Health information"
                emptyStateMessage={localize(messages.emptyState)}>
                <TaForHealthLabelerContainer
                    document={document}
                    results={healthAnalysis?.documents[0]}
                    isLabelerRTL={isDocumentSwaggerRtl(language)}
                    isLabelerWordTokenized={isDocumentSwaggerWordTokenized(language)}
                />
            </ResultsStep>

            <NextStepsStep
                sdkLink={taForHealthSdkLink}
                isResultsEmpty={!healthAnalysis}
                featureName="Health information"
                documentationLink={taForHealthDocLink}
                sampleCodeLink={taForHealthSamplesLink}
                demosToDisplay={['pii', 'ner', 'questionsAnswering']}
                curlCommand={getCurlCommand({
                    language: 'en',
                    text: document,
                    endpoint: 'Healthcare',
                    apiVersion: getVersion(apiVersion, true),
                    lro: true
                })}
            />
        </Stack>
    );
});
